import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Global anonymous session key used to dedupe /track-view for logged-out
 * visitors. Persisted in localStorage so a returning anonymous visitor still
 * counts as the same unique viewer across reloads.
 */
export function anonSessionKey() {
  try {
    let k = localStorage.getItem("dsl_session_key");
    if (!k) {
      k = `anon_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      localStorage.setItem("dsl_session_key", k);
    }
    return k;
  } catch (_e) {
    return `anon_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Fires a single POST /lives/{id}/track-view for the given drop as soon as
 * the component mounts. Deduped server-side per (live_id, viewer_key), so
 * hammering reload is safe — it counts as one unique viewer.
 */
export function useTrackDropView(liveId) {
  useEffect(() => {
    if (!liveId) return;
    const sessionKey = anonSessionKey();
    api.post(`/lives/${liveId}/track-view`, null, {
      headers: { "X-Session-Key": sessionKey },
    }).catch(() => { /* silent — analytics best-effort */ });
  }, [liveId]);
}

/**
 * Public engagement snapshot hook — {likes, liked_by_me}. Polls lightly (every
 * 8s) so the heart count stays fresh without hammering the API. Exposes a
 * toggle() helper the LikeButton uses for optimistic updates.
 */
export function useDropEngagement(liveId) {
  const [state, setState] = useState({ likes: 0, liked_by_me: false, loading: true });

  const refresh = () => {
    if (!liveId) return;
    api.get(`/lives/${liveId}/engagement`)
      .then((r) => setState({ ...r.data, loading: false }))
      .catch(() => setState((s) => ({ ...s, loading: false })));
  };

  useEffect(() => {
    if (!liveId) return undefined;
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveId]);

  const toggle = async () => {
    const before = state;
    // Optimistic
    setState((s) => ({
      ...s,
      liked_by_me: !s.liked_by_me,
      likes: Math.max(0, s.likes + (s.liked_by_me ? -1 : 1)),
    }));
    try {
      const r = await api.post(`/lives/${liveId}/like`);
      setState((s) => ({ ...s, ...r.data }));
    } catch (_e) {
      // Roll back optimistic update on error
      setState(before);
    }
  };

  return { ...state, toggle, refresh };
}
